import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, MapPin } from "lucide-react";
import { WHATSAPP_URL, scrollToSection } from "./Navbar";
import bollo from "../assets/bollo.png";

const HERO_IMG =
  "https://customer-assets-agu9un31.emergentagent.net/job_54b9bf9b-ff46-4262-b661-491fa7f93aaf/artifacts/m1s65o4x_IMG_9536.webp";

const lineReveal = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 1.1, delay: 0.55 + i * 0.18, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen pt-[76px] overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 lg:pt-24 pb-20 relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7 relative z-10">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.35 }}
              className="flex items-center gap-4 text-[0.7rem] uppercase tracking-[0.35em] font-medium text-[#84232a] mb-8"
              data-testid="hero-eyebrow"
            >
              <span className="w-10 h-px bg-[#84232a]/50" />
              Pasticceria artigianale · Trento · dal 1999
            </motion.p>

            <h1 className="font-display font-medium text-black leading-[1.02] text-5xl sm:text-6xl lg:text-7xl tracking-tight" data-testid="hero-title">
              <span className="mask-line">
                <motion.span className="block" custom={0} variants={lineReveal} initial="hidden" animate="visible">
                  L'arte dolce
                </motion.span>
              </span>
              <span className="mask-line">
                <motion.span className="block" custom={1} variants={lineReveal} initial="hidden" animate="visible">
                  nel cuore
                </motion.span>
              </span>
              <span className="mask-line">
                <motion.span className="block italic text-[#84232a]" custom={2} variants={lineReveal} initial="hidden" animate="visible">
                  di Trento.
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.3 }}
              className="font-script text-3xl sm:text-4xl text-[#84232a] mt-7"
              data-testid="hero-script"
            >
              San Vigilio, dal 1999
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.45 }}
              className="text-[#4a423d] text-base sm:text-lg leading-relaxed max-w-md mt-5 font-light"
              data-testid="hero-description"
            >
              Un'oasi di gusto ed eleganza nel centro storico: pasticceria
              artigianale, colazioni e torte su ordinazione curate in ogni dettaglio.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.6 }}
              className="flex flex-wrap items-center gap-4 mt-10"
            >
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-whatsapp"
                className="group flex items-center gap-3 bg-[#84232a] text-[#fbf6f1] px-8 py-4 rounded-full text-sm font-medium hover:bg-[#63191f] transition-colors duration-300"
              >
                <MessageCircle className="w-4 h-4 transition-transform duration-300 group-hover:rotate-12" />
                Prenota su WhatsApp
              </a>
              <a
                href="#prodotti"
                onClick={(e) => scrollToSection(e, "#prodotti")}
                data-testid="hero-cta-scopri"
                className="text-sm font-medium text-black/70 underline underline-offset-8 decoration-[#84232a]/40 hover:decoration-[#84232a] hover:text-[#84232a] transition-colors duration-300"
              >
                Scopri l'assortimento
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.9 }}
              className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mt-12 pt-6 border-t border-[#e6dacb] text-xs text-[#4a423d] tracking-wide"
              data-testid="hero-info-line"
            >
              <span className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-[#84232a] shrink-0" />
                Via S. Vigilio 8, Trento
              </span>
              <span className="hidden sm:block w-1 h-1 rounded-full bg-[#84232a]/40" />
              <span>Mar – Dom 8:00 – 19:30 · Lun chiuso</span>
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.4, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-t-[12rem] rounded-b-none overflow-hidden aspect-[3/4] max-w-md mx-auto lg:ml-auto border border-[#e6dacb]"
              data-testid="hero-image-frame"
            >
              <motion.img
                src={HERO_IMG}
                alt="Il banco della Pasticceria San Vigilio con croissant e mignon artigianali"
                style={{ y: imgY, scale: 1.15 }}
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
            </motion.div>
            <motion.img
              src={bollo}
              alt="Bollo Pasticceria San Vigilio"
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 1.7, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -bottom-10 -left-2 sm:left-2 lg:-left-12 w-28 h-28 sm:w-32 sm:h-32 object-contain drop-shadow-xl"
              style={{ animation: "spin-slow 40s linear infinite" }}
              data-testid="hero-bollo"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
